// Pure domain functions for grouping and diffing version / release-note data
// across storefronts. No side effects; no DOM access.

/**
 * Group rows by version string.
 *
 * Only rows where `available === true` and `version !== null` are considered.
 * Groups are sorted by descending country count (most common version first).
 *
 * @param {import('./normalizeApp.js').RegionRow[]} rows
 * @returns {{ allSame: boolean, groups: Array<{version: string, countries: string[]}> }}
 */
export function groupByVersion(rows) {
  /** @type {Map<string, string[]>} */
  const map = new Map();

  for (const row of rows) {
    if (!row.available || row.version === null) continue;
    const list = map.get(row.version);
    if (list) {
      list.push(row.countryCode);
    } else {
      map.set(row.version, [row.countryCode]);
    }
  }

  if (map.size === 0) {
    return { allSame: true, groups: [] };
  }

  const groups = Array.from(map.entries())
    .map(([version, countries]) => ({ version, countries }))
    .sort((a, b) => b.countries.length - a.countries.length);

  return {
    allSame: map.size === 1,
    groups,
  };
}

/**
 * Identify the lead (majority) version and any storefronts that are out of step
 * with it — the signal of a staggered rollout (e.g. "all on v7.42 except JP on
 * v7.41").
 *
 * Builds on {@link groupByVersion}: the lead version is the one run by the most
 * storefronts (ties resolve to the first one seen), and every other version
 * group is an outlier. `staggered` is true whenever two or more distinct
 * versions are live across the available storefronts.
 *
 * @param {import('./normalizeApp.js').RegionRow[]} rows
 * @returns {{
 *   staggered: boolean,
 *   leadVersion: string|null,
 *   leadCountries: string[],
 *   outliers: Array<{version: string, countries: string[]}>
 * }}
 */
export function findVersionOutliers(rows) {
  const { allSame, groups } = groupByVersion(rows);

  if (groups.length === 0) {
    return { staggered: false, leadVersion: null, leadCountries: [], outliers: [] };
  }

  const [lead, ...outliers] = groups;
  return {
    staggered: !allSame,
    leadVersion: lead.version,
    leadCountries: lead.countries,
    outliers,
  };
}

/**
 * Find rows whose release notes differ from the most common release notes.
 *
 * Only rows where `available === true` and `releaseNotes !== null` are
 * considered. The "most common" notes are the value shared by the most
 * storefronts. Entries whose notes match the most common value are excluded
 * from `uniqueEntries`.
 *
 * @param {import('./normalizeApp.js').RegionRow[]} rows
 * @returns {{
 *   mostCommonNotes: string|null,
 *   uniqueEntries: Array<{countryCode: string, countryName: string, version: string|null, releaseNotes: string}>
 * }}
 */
export function findUniqueReleaseNotes(rows) {
  const eligible = rows.filter((r) => r.available && r.releaseNotes !== null);

  if (eligible.length === 0) {
    return { mostCommonNotes: null, uniqueEntries: [] };
  }

  // Count occurrences of each notes string.
  /** @type {Map<string, number>} */
  const freq = new Map();
  for (const row of eligible) {
    freq.set(row.releaseNotes, (freq.get(row.releaseNotes) ?? 0) + 1);
  }

  // Find the most common notes.
  let mostCommonNotes = null;
  let maxCount = 0;
  for (const [notes, count] of freq.entries()) {
    if (count > maxCount) {
      maxCount = count;
      mostCommonNotes = notes;
    }
  }

  // Unique entries: those that differ from the most common.
  const uniqueEntries = eligible
    .filter((r) => r.releaseNotes !== mostCommonNotes)
    .map((r) => ({
      countryCode: r.countryCode,
      countryName: r.countryName,
      version: r.version,
      releaseNotes: r.releaseNotes,
    }));

  return { mostCommonNotes, uniqueEntries };
}
